import { emailWrapper, dataRow, imageRow, sectionHeader } from './_shared/emailWrapper.js'

/**
 * Generate New Client email HTML
 * @param {Object} data - Client form data
 * @param {Object} agent - Agent info { name, number }
 * @returns {string} Complete HTML email
 */
export function generateNewClientEmailHtml(data, agent) {
  const formatDate = (date) => {
    if (!date) return '—'
    return new Date(date).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const content = `
    <!-- Title -->
    <div style="text-align: center; margin-bottom: 24px;">
      <div style="display: inline-block; background-color: #d1fae5; color: #059669; padding: 8px 16px; border-radius: 20px; font-size: 14px; font-weight: 500;">
        לקוח חדש נוסף למערכת
      </div>
    </div>

    <h2 style="margin: 0 0 8px; color: #1e293b; font-size: 20px; font-weight: 600; text-align: center;">
      ${data.client_name || 'לקוח חדש'}
    </h2>
    <p style="margin: 0 0 24px; color: #64748b; font-size: 14px; text-align: center;">
      נוצר בתאריך ${formatDate(new Date())} על ידי הסוכן ${agent.name}
    </p>

    <!-- Data Table -->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden;">

      ${sectionHeader('פרטי הסוכן', '👤')}
      ${dataRow('מספר סוכן', agent.number, false)}
      ${dataRow('שם הסוכן', agent.name, true)}

      ${sectionHeader('פרטי העסק', '🏪')}
      ${dataRow('שם הלקוח / בית העסק', data.client_name, false)}
      ${dataRow('ח.פ / ע.מ', data.business_id, true)}
      ${dataRow('עיר', data.city, false)}
      ${dataRow('רחוב', data.street, true)}
      ${dataRow('מספר בית', data.house_number, false)}
      ${dataRow('הערות לכתובת', data.address_comments, false)}
      ${dataRow('טלפון בית העסק', data.business_phone, true)}
      ${dataRow('טלפון נייד', data.mobile_phone, false)}
      ${dataRow('פקס', data.fax_number, true)}
      ${dataRow('איש קשר', data.contact_name, false)}

      ${sectionHeader('פרטי בעל העסק', '👔')}
      ${dataRow('שם בעל העסק', data.owner_full_name, false)}
      ${dataRow('תעודת זהות', data.owner_id, true)}
      ${dataRow('תאריך לידה', formatDate(data.owner_birth_date), false)}
      ${dataRow('כתובת מגורים', data.owner_address, true)}

      ${sectionHeader('פרטי העסק הנוספים', '📋')}
      ${dataRow('סוג העסק', data.business_type, false)}
      ${dataRow('וותק העסק', data.business_seniority ? `${data.business_seniority} שנים` : '—', true)}
      ${dataRow("צ'קים חזרו בעבר", data.back_check, false)}
      ${dataRow('מספר קופות', data.kupot_qnt, true)}
      ${dataRow('רמת כשרות', data.kosher_type, false)}

      ${sectionHeader('פרטי תשלום', '💳')}
      ${dataRow('תנאי תשלום', data.payment_type, false)}
      ${data.payment_type && data.payment_type !== 'מזומן' ? dataRow('בנק', data.bank_name, true) : ''}
      ${data.payment_type && data.payment_type !== 'מזומן' ? dataRow('מספר סניף', data.bank_branche_number, false) : ''}
      ${data.payment_type && data.payment_type !== 'מזומן' ? dataRow('מספר חשבון', data.bank_account_number, true) : ''}
      ${data.payment_type && data.payment_type !== 'מזומן' ? dataRow('שם חשבון', data.bank_account_name, false) : ''}
      ${dataRow('ימי אשראי', data.credit_days, true)}
      ${dataRow("מסירת צ'ק", data.check_delivery, false)}

      ${sectionHeader('פרטי ביקורים', '📅')}
      ${dataRow('יום ביקור', data.visit_days, false)}
      ${dataRow('תדירות', data.visit_frequency, true)}
      ${data.visit_frequency === 'פעם בשבועיים' ? dataRow('סוג ביקור', data.visit_frequency_twice_a_week === 'ידני' ? data.visit_type_manual : data.visit_frequency_twice_a_week, false) : ''}
      ${data.visit_frequency === 'פעם בחודש' ? dataRow('סוג ביקור', data.visit_frequency_once_a_month === 'ידני' ? data.visit_type_manual : data.visit_frequency_once_a_month, false) : ''}
      ${data.visit_frequency === 'כמו לקוח' ? dataRow('כמו לקוח מספר', data.visit_like_customer, false) : ''}
      ${dataRow('שעות מוגבלות', data.is_visit_limit, true)}
      ${data.is_visit_limit === 'כן' ? dataRow('טווח שעות', `${data.visit_limit_hour_start} - ${data.visit_limit_hour_end}`, false) : ''}

      ${sectionHeader('צילומים', '📷')}
      ${imageRow('צילום ת"ז', data.owner_id_photo, false)}
      ${imageRow('צילום סרט קופה', data.owner_chash_photo, true)}
      ${imageRow('צילום ערבות', data.owner_guaranty_photo, false)}
      ${imageRow('צילום חזית העסק', data.owner_business_front_photo, true)}
      ${imageRow('חתימה', data.signature, false)}
    </table>

    <!-- Action Button -->
    <div style="text-align: center; margin-top: 32px;">
      <p style="margin: 0 0 16px; color: #64748b; font-size: 14px;">
        לצפייה בפרטים המלאים היכנס למערכת הניהול
      </p>
    </div>
  `

  return emailWrapper(content, `לקוח חדש: ${data.client_name}`)
}

/**
 * Generate Update Client email HTML
 * @param {Object} data - Client form data
 * @param {Object} agent - Agent info { name, number }
 * @param {string} clientNumber - The client number being updated
 * @returns {string} Complete HTML email
 */
export function generateUpdateClientEmailHtml(data, agent, clientNumber) {
  const formatDate = (date) => {
    if (!date) return '—'
    return new Date(date).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const content = `
    <!-- Title -->
    <div style="text-align: center; margin-bottom: 24px;">
      <div style="display: inline-block; background-color: #fef3c7; color: #d97706; padding: 8px 16px; border-radius: 20px; font-size: 14px; font-weight: 500;">
        עדכון פרטי לקוח
      </div>
    </div>

    <h2 style="margin: 0 0 8px; color: #1e293b; font-size: 20px; font-weight: 600; text-align: center;">
      ${data.client_name || 'לקוח'}
    </h2>
    <p style="margin: 0 0 24px; color: #64748b; font-size: 14px; text-align: center;">
      לקוח מס׳ ${clientNumber} עודכן בתאריך ${formatDate(new Date())} על ידי הסוכן ${agent.name}
    </p>

    <!-- Data Table -->
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden;">

      ${sectionHeader('פרטי הסוכן', '👤')}
      ${dataRow('מספר סוכן', agent.number, false)}
      ${dataRow('שם הסוכן', agent.name, true)}

      ${sectionHeader('פרטי העסק', '🏪')}
      ${dataRow('מספר לקוח', clientNumber, false)}
      ${dataRow('שם הלקוח / בית העסק', data.client_name, true)}
      ${dataRow('ח.פ / ע.מ', data.business_id, false)}
      ${dataRow('עיר', data.city, true)}
      ${dataRow('רחוב', data.street, false)}
      ${dataRow('מספר בית', data.house_number, true)}
      ${dataRow('הערות לכתובת', data.address_comments, true)}
      ${dataRow('טלפון בית העסק', data.business_phone, false)}
      ${dataRow('טלפון נייד', data.mobile_phone, true)}
      ${dataRow('פקס', data.fax_number, false)}
      ${dataRow('איש קשר', data.contact_name, true)}

      ${sectionHeader('פרטי בעל העסק', '👔')}
      ${dataRow('שם בעל העסק', data.owner_full_name, false)}
      ${dataRow('תעודת זהות', data.owner_id, true)}
      ${dataRow('תאריך לידה', formatDate(data.owner_birth_date), false)}
      ${dataRow('כתובת מגורים', data.owner_address, true)}

      ${sectionHeader('פרטי העסק הנוספים', '📋')}
      ${dataRow('סוג העסק', data.business_type, false)}
      ${dataRow('וותק העסק', data.business_seniority ? `${data.business_seniority} שנים` : '—', true)}
      ${dataRow("צ'קים חזרו בעבר", data.back_check, false)}
      ${dataRow('מספר קופות', data.kupot_qnt, true)}
      ${dataRow('רמת כשרות', data.kosher_type, false)}

      ${sectionHeader('פרטי תשלום', '💳')}
      ${dataRow('תנאי תשלום', data.payment_type, false)}
      ${data.payment_type && data.payment_type !== 'מזומן' ? dataRow('בנק', data.bank_name, true) : ''}
      ${data.payment_type && data.payment_type !== 'מזומן' ? dataRow('מספר סניף', data.bank_branche_number, false) : ''}
      ${data.payment_type && data.payment_type !== 'מזומן' ? dataRow('מספר חשבון', data.bank_account_number, true) : ''}
      ${data.payment_type && data.payment_type !== 'מזומן' ? dataRow('שם חשבון', data.bank_account_name, false) : ''}
      ${dataRow('ימי אשראי', data.credit_days, true)}
      ${dataRow("מסירת צ'ק", data.check_delivery, false)}

      ${sectionHeader('פרטי ביקורים', '📅')}
      ${dataRow('יום ביקור', data.visit_days, false)}
      ${dataRow('תדירות', data.visit_frequency, true)}
      ${data.visit_frequency === 'פעם בשבועיים' ? dataRow('סוג ביקור', data.visit_frequency_twice_a_week === 'ידני' ? data.visit_type_manual : data.visit_frequency_twice_a_week, false) : ''}
      ${data.visit_frequency === 'פעם בחודש' ? dataRow('סוג ביקור', data.visit_frequency_once_a_month === 'ידני' ? data.visit_type_manual : data.visit_frequency_once_a_month, false) : ''}
      ${data.visit_frequency === 'כמו לקוח' ? dataRow('כמו לקוח מספר', data.visit_like_customer, false) : ''}
      ${dataRow('שעות מוגבלות', data.is_visit_limit, true)}
      ${data.is_visit_limit === 'כן' ? dataRow('טווח שעות', `${data.visit_limit_hour_start} - ${data.visit_limit_hour_end}`, false) : ''}
    </table>

    <!-- Action Button -->
    <div style="text-align: center; margin-top: 32px;">
      <p style="margin: 0 0 16px; color: #64748b; font-size: 14px;">
        לצפייה בפרטים המלאים היכנס למערכת הניהול
      </p>
    </div>
  `

  return emailWrapper(content, `עדכון לקוח: ${data.client_name}`)
}
